<script setup lang="ts">
import {
  RemoteApp,
  ServerAdapter,
  SignInPage,
  createAccountPreferences,
  provideAccountPreferences,
  navigateBrowserTab,
  clearBrowserTab,
} from '@atum-remote/ui';
import { BROWSER_ACTIVITY_INTERVAL_MS, parseConsoleEnvironment } from '@atum-remote/config';
import { defineAsyncComponent, onErrorCaptured, onMounted, onUnmounted, ref } from 'vue';
import TerminalLoadStatus from './TerminalLoadStatus.vue';
import DesktopLoadStatus from './DesktopLoadStatus.vue';
import NyxLoadStatus from './NyxLoadStatus.vue';
const DesktopView = defineAsyncComponent({
  loader: () => import('./DesktopView.vue'),
  loadingComponent: DesktopLoadStatus,
  errorComponent: DesktopLoadStatus,
  delay: 0,
  timeout: 15000,
});
const signInComplete = new URLSearchParams(window.location.search).get('sign-in') === 'complete';
const isDesktop = window.location.pathname === '/desktop';
const TerminalView = defineAsyncComponent({
  loader: () => import('./TerminalView.vue'),
  loadingComponent: TerminalLoadStatus,
  errorComponent: TerminalLoadStatus,
  delay: 0,
  timeout: 15_000,
});
const isTerminal = window.location.pathname === '/terminal';
const NyxView = defineAsyncComponent({
  loader: () => import('./NyxView.vue'),
  loadingComponent: NyxLoadStatus,
  errorComponent: NyxLoadStatus,
  delay: 0,
  timeout: 15_000,
});
const isNyx = window.location.pathname === '/nyx';
// Presentation only: authorization always stays with the server and device grants.
const combinedApp = navigator.userAgent === 'ATUMDesktop/0.2';
const sessionFailed = ref(false);
onErrorCaptured(() => {
  if (!isTerminal && !isDesktop && !isNyx) return;
  sessionFailed.value = true;
  return false;
});
const config = parseConsoleEnvironment(import.meta.env);
const adapter = new ServerAdapter(window.location.origin, config.apiBaseUrl);
const preferences = createAccountPreferences(adapter);
provideAccountPreferences(preferences);

// Every route, including the session pages, sits behind this gate. Nothing renders until the
// server confirms a signed-in session; a signed-out visitor only ever sees the sign-in page.
type Gate = 'checking' | 'signed-out' | 'signed-in' | 'unavailable';
const gate = ref<Gate>('checking');
adapter.onSessionEnded = () => {
  if (gate.value === 'signed-in') signedOut('expired');
};
const configured = ref(false);
const gateError = ref('');
const reason = ref<'expired' | 'signed-out'>();
const redirecting = ref(false);
let signInRevision = 0;
let check: AbortController | undefined;
let revision = 0;
let sessionTimer: ReturnType<typeof setInterval> | undefined;

async function checkSession(background = false) {
  if (check) return;
  const controller = new AbortController();
  check = controller;
  const current = ++revision;
  const wasSignedIn = gate.value === 'signed-in';
  if (!background) gate.value = 'checking';
  gateError.value = '';
  try {
    const session = await adapter.session(controller.signal);
    if (current !== revision) return;
    configured.value = session.configured;
    if (!session.account) {
      preferences.clear();
      if (wasSignedIn) reason.value = 'expired';
      gate.value = 'signed-out';
      return;
    }
    reason.value = undefined;
    if (!wasSignedIn) preferences.clear();
    void preferences.load().catch(() => {});
    gate.value = 'signed-in';
  } catch (cause) {
    if (current !== revision) return;
    if (cause instanceof DOMException && cause.name === 'AbortError') return;
    gate.value = 'unavailable';
    gateError.value =
      cause instanceof Error ? cause.message : 'The server could not be reached. Try again.';
  } finally {
    if (current === revision) check = undefined;
  }
}
function cancelSignIn() {
  signInRevision++;
  adapter.cancelSignIn();
  redirecting.value = false;
}
async function signIn() {
  if (!configured.value || redirecting.value) return;
  const current = ++signInRevision;
  redirecting.value = true;
  gateError.value = '';
  try {
    await adapter.signIn();
    if (current !== signInRevision) return;
    redirecting.value = false;
    await checkSession();
  } catch (error) {
    if (current !== signInRevision) return;
    redirecting.value = false;
    if (error instanceof DOMException && error.name === 'AbortError') return;
    gateError.value = error instanceof Error ? error.message : 'Sign-in could not start.';
  }
}
let activityTimer: ReturnType<typeof setTimeout> | undefined;
let lastActivityReport = 0;
function reportActivity() {
  activityTimer = undefined;
  if (gate.value !== 'signed-in') return;
  lastActivityReport = Date.now();
  void adapter
    .activity()
    .then(() => {
      if (gate.value === 'signed-in') window.dispatchEvent(new Event('atum-remote-activity'));
    })
    .catch(() => {});
}
function humanActivity(event: Event) {
  if (!event.isTrusted || document.hidden || gate.value !== 'signed-in' || activityTimer) return;
  const delay = BROWSER_ACTIVITY_INTERVAL_MS - (Date.now() - lastActivityReport);
  if (delay <= 0) reportActivity();
  else activityTimer = setTimeout(reportActivity, delay);
}
const activityEvents = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;
function signedOut(why: 'expired' | 'signed-out') {
  cancelSignIn();
  preferences.clear();
  revision++;
  check?.abort();
  check = undefined;
  configured.value = adapter.signInConfigured;
  redirecting.value = false;
  reason.value = why;
  gate.value = 'signed-out';
  clearTimeout(activityTimer);
  activityTimer = undefined;
}
function checkVisibleSession(maxAge = 60_000) {
  if (document.hidden || gate.value !== 'signed-in') return;
  // Inventory checks may keep identity fresh indefinitely. Account settings have their own clock.
  if (Date.now() - adapter.sessionCheckedAt >= maxAge) void checkSession(true);
  else if (!preferences.busy.value && Date.now() - preferences.checkedAt.value >= maxAge)
    void preferences.load().catch(() => {});
}
const onVisibility = () => checkVisibleSession(30_000);
function onPageShow(event: PageTransitionEvent) {
  if (!event.persisted) return;
  // BFCache restores the JavaScript heap too; force a fresh tab boundary before any request.
  clearBrowserTab();
  signedOut('expired');
  void checkSession();
}
function onAppNavigation(event: MouseEvent) {
  if (
    !event.isTrusted ||
    event.button !== 0 ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    event.altKey
  )
    return;
  const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (
    !(anchor instanceof HTMLAnchorElement) ||
    anchor.download ||
    (anchor.target && anchor.target !== '_self')
  )
    return;
  const target = new URL(anchor.href);
  if (
    target.origin !== window.location.origin ||
    !/^\/(?:terminal|desktop)?$/.test(target.pathname) ||
    (target.pathname === window.location.pathname && target.search === window.location.search)
  )
    return;
  event.preventDefault();
  navigateBrowserTab(target.href);
}
onMounted(() => {
  if (signInComplete) {
    window.close();
    return;
  }
  window.addEventListener('click', onAppNavigation, true);
  for (const event of activityEvents)
    window.addEventListener(event, humanActivity, { capture: true, passive: true });
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', onPageShow);
  sessionTimer = setInterval(checkVisibleSession, 15_000);
  void checkSession();
});
onUnmounted(() => {
  cancelSignIn();
  window.removeEventListener('click', onAppNavigation, true);
  clearTimeout(activityTimer);
  for (const event of activityEvents) window.removeEventListener(event, humanActivity, true);
  revision++;
  check?.abort();
  clearInterval(sessionTimer);
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('pageshow', onPageShow);
});
</script>
<template>
  <main v-if="signInComplete" class="signin">
    <section class="signin-card panel">
      <h1>Sign-in complete</h1>
      <p>Return to your ATUM Remote tab. You can close this window.</p>
    </section>
  </main>
  <SignInPage
    v-else-if="gate !== 'signed-in'"
    :state="gate"
    :configured="configured"
    :error="gateError"
    :reason="reason"
    :busy="redirecting"
    :return-to-atum="combinedApp"
    @sign-in="signIn"
    @cancel-sign-in="cancelSignIn"
    @retry="checkSession"
  />
  <template v-else>
    <DesktopLoadStatus v-if="isDesktop && sessionFailed" failed />
    <DesktopView
      v-else-if="isDesktop"
      :api-base="adapter.apiBaseUrl"
      @unauthorized="signedOut('expired')"
    />
    <NyxLoadStatus v-else-if="isNyx && sessionFailed" failed />
    <NyxView v-else-if="isNyx" :adapter="adapter" />
    <TerminalLoadStatus v-else-if="isTerminal && sessionFailed" failed />
    <TerminalView
      v-else-if="isTerminal"
      :api-base="adapter.apiBaseUrl"
      @unauthorized="signedOut('expired')"
    />
    <RemoteApp v-else :adapter="adapter" :combined-app="combinedApp" @signed-out="signedOut" />
  </template>
</template>
